/**
 * App Shell Component - Dual Sidebar Layout for TrustGrid
 * Root layout: Primary Sidebar (Truvex modules) + Secondary Sidebar (TrustGrid nav) + Content
 * Responsive: Secondary overlays on small screens (<1200px)
 */

import React, { useState, useEffect } from 'react';
import { Layout } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import PrimarySidebar from './PrimarySidebar';
import TrustGridSidebar from './TrustGridSidebar';
import LibrarySidebar from './LibrarySidebar';
import AIMLSidebar from './AIMLSidebar';
import QualitySidebar from './QualitySidebar';
import AdministrationSidebar from './AdministrationSidebar';

const { Content } = Layout;

/**
 * AppShell Component with Dual Sidebar
 *
 * @param {ReactNode} children - Page content
 * @param {function} onLogout - Logout handler (passed to PrimarySidebar)
 */
function AppShell({ children, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Track which secondary sidebar is open (null = none, 'trustgrid' = TrustGrid sidebar, etc.)
  const [activeSecondary, setActiveSecondary] = useState(null); // Start closed
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  // Track primary sidebar expanded state
  const [primaryExpanded, setPrimaryExpanded] = useState(false);

  // Responsive behavior: detect screen size
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1200);
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle module icon click from primary sidebar
  const handleModuleClick = (moduleKey) => {
    // Modules with secondary sidebars
    const modulesWithSecondary = {
      '/ai-ml': 'aiml',
      '/quality-compliance': 'quality',
      '/library': 'library',
      '/trustgrid': 'trustgrid',
      '/administration': 'administration',
    };

    if (modulesWithSecondary[moduleKey]) {
      const secondaryKey = modulesWithSecondary[moduleKey];
      // Toggle secondary sidebar if already open for this module, otherwise open it
      setActiveSecondary(activeSecondary === secondaryKey ? null : secondaryKey);
      // Navigate to default route for the module
      if (moduleKey === '/ai-ml') navigate('/ai-ml/test-cases');
      else if (moduleKey === '/quality-compliance') navigate('/quality-compliance/quality');
      else if (moduleKey === '/library') navigate('/library/templates');
      else if (moduleKey === '/administration') navigate('/administration/users');
      // TrustGrid already navigates via its own logic
    } else {
      // For modules without secondary (Dashboard, Templates), close secondary and navigate
      setActiveSecondary(null);
      navigate(moduleKey);
    }
  };

  // Close secondary sidebar (called from secondary's menu item click)
  const closeSecondary = () => {
    setActiveSecondary(null);
  };

  // Calculate content offset based on sidebar states
  const railWidth = primaryExpanded ? 200 : 56; // Primary rail width
  const panelWidth = isSmallScreen || !activeSecondary ? 0 : 240; // Secondary panel width
  const totalMargin = railWidth + panelWidth;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Primary Rail - Icon/expandable navigation (always visible) */}
      <PrimarySidebar
        onModuleClick={handleModuleClick}
        expanded={primaryExpanded}
        setExpanded={setPrimaryExpanded}
      />

      {/* Secondary Sidebars - Conditional rendering based on activeSecondary */}
      {activeSecondary === 'aiml' && (
        <AIMLSidebar
          onClose={closeSecondary}
          primaryExpanded={primaryExpanded}
        />
      )}
      {activeSecondary === 'quality' && (
        <QualitySidebar
          onClose={closeSecondary}
          primaryExpanded={primaryExpanded}
        />
      )}
      {activeSecondary === 'library' && (
        <LibrarySidebar
          onClose={closeSecondary}
          primaryExpanded={primaryExpanded}
        />
      )}
      {activeSecondary === 'trustgrid' && (
        <TrustGridSidebar
          onClose={closeSecondary}
          primaryExpanded={primaryExpanded}
        />
      )}
      {activeSecondary === 'administration' && (
        <AdministrationSidebar
          onClose={closeSecondary}
          primaryExpanded={primaryExpanded}
        />
      )}

      {/* Main Content Area */}
      <Layout
        style={{
          marginLeft: totalMargin,
          marginTop: 0,
          transition: 'margin-left 0.3s ease-in-out',
          minHeight: '100vh',
        }}
      >
        <Content
          className="main-content-scroll"
          onClick={() => {
            if (activeSecondary) {
              closeSecondary();
            }
          }}
          style={{
            overflowY: 'scroll',
            overflowX: 'hidden',
            height: '100vh',
            position: 'relative',
            backgroundColor: '#ffffff',
            padding: '32px',
            maxWidth: '1400px',
            margin: '0 auto',
            width: '100%',
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
}

export default AppShell;
